import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';

const STORAGE_BUCKET = 'property-images';

function fileExtension(file) {
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot >= 0 && name.length - dot <= 6) {
    return name.slice(dot).toLowerCase();
  }
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/gif') return '.gif';
  return '.jpg';
}

async function uploadImage(supabaseClient, propertyId, file, basename) {
  const path = `${propertyId}/${basename}-${Date.now()}${fileExtension(file)}`;
  const { error } = await supabaseClient.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data } = supabaseClient.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

function PropertyNew() {
  const navigate = useNavigate();
  const { id: editPropertyId } = useParams();
  const isEdit = Boolean(editPropertyId);

  const mainImageInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);
  const [mainImage, setMainImage] = React.useState(null);
  const [galleryImages, setGalleryImages] = React.useState([]);
  const [rooms, setRooms] = React.useState([
    { id: 1, name: '', size: '' },
  ]);

  const [name, setName] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [price, setPrice] = React.useState('');
  const [roomCount, setRoomCount] = React.useState('');
  const [occupancyStatus, setOccupancyStatus] = React.useState('high');

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [loadInitial, setLoadInitial] = React.useState(() =>
    Boolean(editPropertyId)
  );
  const [loadError, setLoadError] = React.useState('');

  const mainImageRef = React.useRef(mainImage);
  const galleryImagesRef = React.useRef(galleryImages);
  mainImageRef.current = mainImage;
  galleryImagesRef.current = galleryImages;

  React.useEffect(() => {
    return () => {
      const m = mainImageRef.current;
      if (m?.url && m.url.startsWith('blob:')) URL.revokeObjectURL(m.url);
      galleryImagesRef.current.forEach((g) => {
        if (g.url && g.url.startsWith('blob:')) URL.revokeObjectURL(g.url);
      });
    };
  }, []);

  React.useEffect(() => {
    if (!isEdit || !editPropertyId) {
      setLoadInitial(false);
      setLoadError('');
      return undefined;
    }

    if (!supabase) {
      setLoadInitial(false);
      setLoadError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadInitial(true);
      setLoadError('');
      const [propRes, roomsRes, galRes] = await Promise.all([
        supabase.from('properties').select('*').eq('id', editPropertyId).single(),
        supabase
          .from('property_rooms')
          .select('*')
          .eq('property_id', editPropertyId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('property_gallery_images')
          .select('*')
          .eq('property_id', editPropertyId)
          .order('sort_order', { ascending: true }),
      ]);

      if (cancelled) return;

      if (propRes.error) {
        setLoadError(propRes.error.message);
        setLoadInitial(false);
        return;
      }

      const prop = propRes.data;
      setName(prop.name || '');
      setLocation(prop.location || '');
      setPrice(
        prop.price_per_month != null ? String(prop.price_per_month) : ''
      );
      setRoomCount(
        prop.room_count != null && prop.room_count !== ''
          ? String(prop.room_count)
          : ''
      );
      setOccupancyStatus(prop.occupancy_status || 'high');

      if (prop.main_image_url) {
        setMainImage({ file: null, url: prop.main_image_url });
      } else {
        setMainImage(null);
      }

      const gRows = galRes.data || [];
      setGalleryImages(
        gRows.map((row) => ({
          id: `db-gal-${row.id}`,
          dbId: row.id,
          file: null,
          url: row.image_url,
        }))
      );

      const rRows = roomsRes.data || [];
      if (rRows.length === 0) {
        setRooms([{ id: Date.now(), name: '', size: '' }]);
      } else {
        setRooms(
          rRows.map((row) => ({
            id: row.id,
            name: row.room_name || '',
            size:
              row.size_sqm != null && row.size_sqm !== ''
                ? String(row.size_sqm)
                : '',
          }))
        );
      }

      setLoadInitial(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editPropertyId]);

  const onMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setMainImage((prev) => {
      if (prev?.url && prev.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file) };
    });
    e.target.value = '';
  };

  const clearMainImage = () => {
    setMainImage((prev) => {
      if (prev?.url && prev.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
      return null;
    });
    if (mainImageInputRef.current) mainImageInputRef.current.value = '';
  };

  const onGalleryChange = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (!files.length) return;
    setGalleryImages((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
    e.target.value = '';
  };

  const removeGalleryImage = (id) => {
    setGalleryImages((prev) => {
      const item = prev.find((g) => g.id === id);
      if (item?.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      return prev.filter((g) => g.id !== id);
    });
  };

  const addRoom = () => {
    setRooms((prev) => [
      ...prev,
      { id: Date.now(), name: '', size: '' },
    ]);
  };

  const updateRoom = (id, field, value) => {
    setRooms((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const deletePropertyIfAny = async (propertyId) => {
    if (!propertyId || !supabase) return;
    await supabase.from('properties').delete().eq('id', propertyId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError('Please enter a property name.');
      return;
    }

    setSaving(true);
    let propertyId = null;
    let createdNewProperty = false;

    try {
      const priceNum = Math.max(0, Math.floor(Number(price) || 0));
      let roomCountVal = null;
      if (roomCount.trim() !== '') {
        const n = Math.floor(Number(roomCount));
        roomCountVal = Number.isFinite(n) && n >= 0 ? n : null;
      }

      const roomRows = (pid) =>
        rooms
          .map((r, i) => ({
            property_id: pid,
            room_name: (r.name || '').trim(),
            size_sqm:
              r.size === '' || r.size === undefined
                ? null
                : Number(r.size),
            sort_order: i,
          }))
          .filter(
            (row) =>
              row.room_name !== '' ||
              (row.size_sqm != null && !Number.isNaN(row.size_sqm))
          );

      if (isEdit) {
        propertyId = editPropertyId;

        let mainUrl = null;
        if (mainImage?.file) {
          mainUrl = await uploadImage(
            supabase,
            propertyId,
            mainImage.file,
            'main'
          );
        } else if (mainImage?.url) {
          mainUrl = mainImage.url;
        }

        const { error: upErr } = await supabase
          .from('properties')
          .update({
            name: trimmedName,
            location: location.trim(),
            price_per_month: priceNum,
            room_count: Number.isFinite(roomCountVal) ? roomCountVal : null,
            occupancy_status: occupancyStatus,
            main_image_url: mainUrl,
          })
          .eq('id', propertyId);
        if (upErr) throw upErr;

        const { error: delGalErr } = await supabase
          .from('property_gallery_images')
          .delete()
          .eq('property_id', propertyId);
        if (delGalErr) throw delGalErr;

        for (let i = 0; i < galleryImages.length; i += 1) {
          const item = galleryImages[i];
          let url = item.url;
          if (item.file) {
            url = await uploadImage(
              supabase,
              propertyId,
              item.file,
              `gallery-${i}`
            );
          }
          const { error: galErr } = await supabase
            .from('property_gallery_images')
            .insert({
              property_id: propertyId,
              image_url: url,
              sort_order: i,
            });
          if (galErr) throw galErr;
        }

        const { error: delRoomsErr } = await supabase
          .from('property_rooms')
          .delete()
          .eq('property_id', propertyId);
        if (delRoomsErr) throw delRoomsErr;

        const toInsertRooms = roomRows(propertyId);
        if (toInsertRooms.length > 0) {
          const { error: roomsErr } = await supabase
            .from('property_rooms')
            .insert(toInsertRooms);
          if (roomsErr) throw roomsErr;
        }
      } else {
        const { data: propRow, error: insertErr } = await supabase
          .from('properties')
          .insert({
            name: trimmedName,
            location: location.trim(),
            price_per_month: priceNum,
            room_count: Number.isFinite(roomCountVal) ? roomCountVal : null,
            occupancy_status: occupancyStatus,
            main_image_url: null,
          })
          .select('id')
          .single();

        if (insertErr) throw insertErr;
        propertyId = propRow.id;
        createdNewProperty = true;

        if (mainImage?.file) {
          const mainUrl = await uploadImage(
            supabase,
            propertyId,
            mainImage.file,
            'main'
          );
          const { error: upMainErr } = await supabase
            .from('properties')
            .update({ main_image_url: mainUrl })
            .eq('id', propertyId);
          if (upMainErr) throw upMainErr;
        }

        for (let i = 0; i < galleryImages.length; i += 1) {
          const item = galleryImages[i];
          if (!item.file) continue;
          const url = await uploadImage(
            supabase,
            propertyId,
            item.file,
            `gallery-${i}`
          );
          const { error: galErr } = await supabase
            .from('property_gallery_images')
            .insert({
              property_id: propertyId,
              image_url: url,
              sort_order: i,
            });
          if (galErr) throw galErr;
        }

        const toInsertRooms = roomRows(propertyId);
        if (toInsertRooms.length > 0) {
          const { error: roomsErr } = await supabase
            .from('property_rooms')
            .insert(toInsertRooms);
          if (roomsErr) throw roomsErr;
        }
      }

      navigate('/properties');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      if (createdNewProperty) await deletePropertyIfAny(propertyId);
      const msg =
        err?.message ||
        err?.error_description ||
        'Could not save property. Check Supabase tables, RLS, and Storage bucket.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="properties-root">
      <UniversalHeader />

      <div className="properties-shell">
        <header className="properties-header">
          <div>
            <h1 className="properties-title">
              {isEdit ? 'Edit property' : 'Add new property'}
            </h1>
            <p className="properties-subtitle">
              {isEdit
                ? 'Update listing details, images, and rooms, then save your changes.'
                : 'Capture key details about a new building, then add rooms and documents.'}
            </p>
          </div>

          <div className="properties-actions">
            <button
              type="button"
              className="btn-ghost properties-btn"
              disabled={saving || loadInitial}
              onClick={() => navigate('/properties')}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="property-new-form"
              className="btn-primary properties-btn"
              disabled={saving || loadInitial}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save property'}
            </button>
          </div>
        </header>

        {saveError ? (
          <p className="properties-save-error" role="alert">
            {saveError}
          </p>
        ) : null}

        {loadError ? (
          <p className="properties-save-error" role="alert">
            {loadError}{' '}
            <button
              type="button"
              className="btn-ghost properties-btn"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/properties')}
            >
              Back to properties
            </button>
          </p>
        ) : null}

        <main className="properties-layout properties-layout--single">
          <section className="properties-detail">
            <div className="properties-detail-card">
              {isEdit && loadInitial ? (
                <p className="properties-list-empty" style={{ margin: '20px 0' }}>
                  Loading property…
                </p>
              ) : null}
              <form
                id="property-new-form"
                className="properties-add-form"
                onSubmit={handleSubmit}
                style={{
                  display:
                    isEdit && (loadInitial || loadError) ? 'none' : undefined,
                }}
              >
                <div className="field-wrapper">
                  <label className="field-label" htmlFor="prop-name">
                    Property name
                  </label>
                  <div className="input-shell">
                    <input
                      id="prop-name"
                      className="input-element"
                      type="text"
                      placeholder="e.g. Elm Court"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="field-wrapper">
                  <label className="field-label" htmlFor="prop-location">
                    Location
                  </label>
                  <div className="input-shell">
                    <input
                      id="prop-location"
                      className="input-element"
                      type="text"
                      placeholder="Neighborhood, city"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                </div>

                <div className="field-wrapper">
                  <label className="field-label" htmlFor="prop-price">
                    Price (£ / month)
                  </label>
                  <div className="input-shell">
                    <input
                      id="prop-price"
                      className="input-element"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="e.g. 1850"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </div>
                </div>

                <div className="properties-media">
                  <div className="properties-media-header">
                    <span>Property images</span>
                    <span className="properties-media-hint">
                      One main photo for listings; add more for the gallery.
                    </span>
                  </div>

                  <div className="properties-media-block">
                    <div className="field-wrapper">
                      <label className="field-label" htmlFor="prop-main-image">
                        Main image
                      </label>
                      <input
                        ref={mainImageInputRef}
                        id="prop-main-image"
                        className="properties-media-input"
                        type="file"
                        accept="image/*"
                        onChange={onMainImageChange}
                      />
                      {mainImage ? (
                        <div className="properties-media-main-preview">
                          <img
                            src={mainImage.url}
                            alt="Main listing preview"
                            className="properties-media-main-img"
                          />
                          <div className="properties-media-main-actions">
                            <button
                              type="button"
                              className="btn-ghost properties-media-btn"
                              onClick={() => mainImageInputRef.current?.click()}
                            >
                              Replace image
                            </button>
                            <button
                              type="button"
                              className="btn-ghost properties-media-btn properties-media-btn--danger"
                              onClick={clearMainImage}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="properties-media-placeholder"
                          onClick={() => mainImageInputRef.current?.click()}
                        >
                          <span
                            className="properties-media-placeholder-icon"
                            aria-hidden
                          >
                            +
                          </span>
                          <span>Upload main listing photo</span>
                          <span className="properties-media-placeholder-sub">
                            JPG, PNG or WebP
                          </span>
                        </button>
                      )}
                    </div>

                    <div className="field-wrapper properties-media-gallery-wrap">
                      <label className="field-label" htmlFor="prop-gallery">
                        Gallery images
                      </label>
                      <input
                        ref={galleryInputRef}
                        id="prop-gallery"
                        className="properties-media-input"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onGalleryChange}
                      />
                      <div className="properties-media-gallery-toolbar">
                        <button
                          type="button"
                          className="btn-ghost properties-room-add"
                          onClick={() => galleryInputRef.current?.click()}
                        >
                          + Add gallery photos
                        </button>
                        {galleryImages.length > 0 && (
                          <span className="properties-media-count">
                            {galleryImages.length} photo
                            {galleryImages.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                      {galleryImages.length > 0 && (
                        <ul
                          className="properties-media-gallery-grid"
                          aria-label="Gallery previews"
                        >
                          {galleryImages.map((item, index) => (
                            <li
                              key={item.id}
                              className="properties-media-gallery-item"
                            >
                              <img
                                src={item.url}
                                alt={`Gallery preview ${index + 1}`}
                              />
                              <button
                                type="button"
                                className="properties-media-gallery-remove"
                                aria-label="Remove from gallery"
                                onClick={() => removeGalleryImage(item.id)}
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="prop-rooms">
                      Number of rooms
                    </label>
                    <div className="input-shell">
                      <input
                        id="prop-rooms"
                        className="input-element"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="e.g. 32"
                        value={roomCount}
                        onChange={(e) => setRoomCount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="prop-status">
                      Occupancy status
                    </label>
                    <div className="input-shell">
                      <select
                        id="prop-status"
                        className="input-element"
                        value={occupancyStatus}
                        onChange={(e) => setOccupancyStatus(e.target.value)}
                      >
                        <option value="high">High (90%+)</option>
                        <option value="medium">Stable (75–90%)</option>
                        <option value="low">Needs attention (&lt;75%)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="properties-rooms">
                  <div className="properties-rooms-header">
                    <span>Add rooms</span>
                  </div>
                  <div className="properties-rooms-form">
                    {rooms.map((room, index) => (
                      <div className="properties-room-row" key={room.id}>
                        <div className="field-wrapper">
                          <label
                            className="field-label"
                            htmlFor={`room-name-${room.id}`}
                          >
                            Room name {rooms.length > 1 ? `#${index + 1}` : ''}
                          </label>
                          <div className="input-shell">
                            <input
                              id={`room-name-${room.id}`}
                              className="input-element"
                              type="text"
                              placeholder="e.g. 2B"
                              value={room.name}
                              onChange={(e) =>
                                updateRoom(room.id, 'name', e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="field-wrapper">
                          <label
                            className="field-label"
                            htmlFor={`room-size-${room.id}`}
                          >
                            Size (sqm)
                          </label>
                          <div className="input-shell">
                            <input
                              id={`room-size-${room.id}`}
                              className="input-element"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="e.g. 48"
                              value={room.size}
                              onChange={(e) =>
                                updateRoom(room.id, 'size', e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-ghost properties-room-add"
                      onClick={addRoom}
                    >
                      + Add another room
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default PropertyNew;
